import { useApolloClient, useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ContextLine,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  entryFromEventFragment,
  PrimaryFullScreenSpinner,
  StatusUI,
  Textarea,
  TimeLog,
  Txt,
  toast,
  UserProfile,
} from "@repo/ui";
import { NotFoundError, neverFunc, superjsonStringify } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { RiskRatingPill } from "~/components/TransactionMonitoring/RiskRatingPill";
import { RulePill } from "~/components/TransactionMonitoring/RulePill";
import { TransactionMonitoringEvaluationCard } from "~/components/TransactionMonitoring/TransactionMonitoringEvaluationCard";
import { TransactionMonitoringFindingItemTargetCard } from "~/components/TransactionMonitoring/TransactionMonitoringFindingItemTargetCard";
import { graphql } from "~/gql/client";
import type {
  AdminTransactionMonitoringFindingDetailFragment,
  TransactionMonitoringFindingItemTargetCardFieldsFragment,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/transaction-monitoring_/findings_/$findingId",
)({
  head: () => opsHead("Transaction Monitoring Finding"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment AdminTransactionMonitoringFindingDetail on TransactionMonitoringFinding {
    id
    shortRef
    organisation {
      ...OrganisationCardFields
    }
    rule {
      id
      label
      description
      threshold
      isBlocking
    }
    reason
    riskRating
    status
    detectedAt
    evaluatedAt
    clearedAt
    clearedReason
    clearedBy {
      id
      firstName
      lastName
      email
      avatarUrl
      ...UserProfileUI
    }
    evaluation {
      id
      ...TransactionMonitoringEvaluationCardFields
    }
    items {
      id
      target {
        ...TransactionMonitoringFindingItemTargetCardFields
      }
    }
    events {
      id
      ...TimeLogEventEntry
    }
  }
`);

type FindingItemRow = {
  id: string;
  kind: string;
  target?: TransactionMonitoringFindingItemTargetCardFieldsFragment;
};

function View() {
  const { findingId } = Route.useParams();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransactionMonitoringFindingDetail($findingId: ID!) {
        node(id: $findingId) {
          ...AdminTransactionMonitoringFindingDetail
        }
      }
    `),
    {
      variables: {
        findingId,
      },
    },
  );

  if (!data.node || data.node.__typename !== "TransactionMonitoringFinding") {
    throw new NotFoundError();
  }

  const finding = data.node;

  useOpsTitle(`Finding #${finding.shortRef}`);

  return (
    <AdminPageContainer>
      <div className="flex flex-col">
        <AdminPageHeader
          title={`Finding #${finding.shortRef}`}
          subtitle={<span className="font-mono">{finding.id}</span>}
          contextLine={
            <ContextLine
              current={`#${finding.shortRef}`}
              ancestors={[
                {
                  label: "Transaction Monitoring",
                  path: "/transaction-monitoring",
                },
                {
                  label: "Findings",
                  path: "/transaction-monitoring/findings",
                },
              ]}
            />
          }
          buttons={
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-4">
                <ClearFindingAction finding={finding} />
                <EscalateFindingAction finding={finding} />
              </div>
            </div>
          }
        />

        <div className="flex flex-col gap-6">
          {finding.rule.isBlocking && (
            <ProcessingBlockAlert status={finding.status} />
          )}

          <div className="flex flex-row justify-between gap-6">
            <div className="flex w-full flex-col">
              <FindingDetailsCard finding={finding} />
            </div>
            <div className="flex w-full flex-col gap-6">
              <FindingItemsCard finding={finding} />
              <FindingEventLogCard finding={finding} />
            </div>
          </div>
        </div>
      </div>
    </AdminPageContainer>
  );
}

function ProcessingBlockAlert({
  status,
}: {
  status: AdminTransactionMonitoringFindingDetailFragment["status"];
}) {
  switch (status) {
    case "CLEARED":
      return null;
    case "OPEN":
      return (
        <Alert
          kind="error"
          title="Processing is blocked"
          description="The linked finding items are blocked from processing transactions while this finding remains open. Processing will stay blocked until the finding is cleared."
        />
      );
    case "ESCALATED":
      return (
        <Alert
          kind="warning"
          title="Processing is blocked pending escalation"
          description="The linked finding items are blocked from processing transactions while this finding remains escalated. Processing will stay blocked until the finding is cleared."
        />
      );
    default:
      return neverFunc(status);
  }
}

function FindingDetailsCard({
  finding,
}: {
  finding: AdminTransactionMonitoringFindingDetailFragment;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
        <CardDescription>
          Status, rule, timestamps, and resolution information for this finding.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <DetailRow
          label="Status"
          value={<StatusUI status={finding.status} />}
        />
        <DetailRow
          label="Organisation"
          value={
            finding.organisation ? (
              <OrganisationCard
                organisation={finding.organisation}
                linkTo={`/organisations/${finding.organisation.id}`}
              />
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Risk"
          value={<RiskRatingPill riskRating={finding.riskRating} />}
        />
        <DetailRow label="Rule" value={<RulePill ruleId={finding.rule.id} />} />
        <DetailRow
          label="Label"
          value={<Txt variant="body">{finding.rule.label}</Txt>}
        />
        <DetailRow
          label="Description"
          value={<Txt variant="body">{finding.rule.description}</Txt>}
        />
        <DetailRow
          label="Threshold"
          value={<Txt variant="body">{finding.rule.threshold}</Txt>}
        />
        <DetailRow
          label="Reason"
          value={<Txt variant="body">{finding.reason ?? "N/A"}</Txt>}
        />
        <DetailRow
          label="Detected at"
          value={<DateUI date={finding.detectedAt} variant="DATETIME_TZ" />}
        />
        <DetailRow
          label="Evaluation"
          value={
            finding.evaluation ? (
              <TransactionMonitoringEvaluationCard
                evaluation={finding.evaluation}
              />
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Evaluated at"
          value={<DateUI date={finding.evaluatedAt} variant="DATETIME_TZ" />}
        />
        <DetailRow
          label="Cleared at"
          value={
            finding.clearedAt ? (
              <DateUI date={finding.clearedAt} variant="DATETIME_TZ" />
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Cleared by"
          value={
            finding.clearedBy ? (
              <div className="w-fit">
                <UserProfile variant="pill" {...finding.clearedBy} />
              </div>
            ) : (
              <Txt variant="body">-</Txt>
            )
          }
        />
        <DetailRow
          label="Cleared reason"
          value={<Txt variant="body">{finding.clearedReason ?? "-"}</Txt>}
        />
      </CardContent>
    </Card>
  );
}

function getFindingItemRow(
  id: string,
  target:
    | TransactionMonitoringFindingItemTargetCardFieldsFragment
    | null
    | undefined,
): FindingItemRow {
  if (!target) {
    return {
      id,
      kind: "Unknown",
    };
  }

  switch (target.__typename) {
    case "Transfer":
      return {
        id,
        kind: "Transfer",
        target,
      };
    case "Deposit":
      return {
        id,
        kind: "Deposit",
        target,
      };
    case "Organisation":
      return {
        id,
        kind: "Organisation",
        target,
      };
    case "ExternalBankAccountAsset":
    case "FixedIncomeFundAsset":
    case "FXAsset":
    case "BankAccountAsset":
    case "TermDepositAsset":
      return {
        id,
        kind: "Asset",
        target,
      };
    default:
      return neverFunc(target);
  }
}

const itemColumns: ColumnDef<FindingItemRow>[] = [
  {
    id: "kind",
    accessorFn: (row) => row.kind,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Kind" />
    ),
    enableSorting: false,
  },
  {
    id: "findingItem",
    accessorFn: (row) => row.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Entity" />
    ),
    cell: ({ row }) => {
      if (!row.original.target) {
        return <Txt variant="body">-</Txt>;
      }
      return (
        <div className="flex flex-row items-center justify-between gap-2">
          <TransactionMonitoringFindingItemTargetCard
            target={row.original.target}
          />
          <FindingItemContextMenu target={row.original.target} />
        </div>
      );
    },
    enableSorting: false,
  },
];

type FindingItemContextOption = {
  label: string;
  to: string;
};

function getFindingItemContextOptions(
  target: TransactionMonitoringFindingItemTargetCardFieldsFragment,
): FindingItemContextOption[] {
  switch (target.__typename) {
    case "Transfer":
      return target.transferGroup
        ? [
            {
              label: "View transfer group",
              to: `/transfer-groups/${target.transferGroup.id}`,
            },
          ]
        : [];
    case "Deposit":
      return [];
    case "Organisation":
      return [
        {
          label: "View transactions",
          to: `/transactions?_ps_organisationIds=${encodeURIComponent(
            superjsonStringify([target.id]),
          )}`,
        },
      ];
    case "ExternalBankAccountAsset":
    case "FixedIncomeFundAsset":
    case "FXAsset":
    case "BankAccountAsset":
    case "TermDepositAsset":
      return [
        {
          label: "View transactions",
          to: `/transactions?_ps_assetIds=${encodeURIComponent(
            superjsonStringify([target.id]),
          )}`,
        },
      ];
    default:
      return neverFunc(target);
  }
}

function FindingItemContextMenu({
  target,
}: {
  target: TransactionMonitoringFindingItemTargetCardFieldsFragment;
}) {
  const options = getFindingItemContextOptions(target);
  if (options.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="secondary">
          <MoreHorizontal className="size-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((option) => (
          <DropdownMenuItem key={option.to} asChild>
            <Link to={option.to}>{option.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FindingItemsCard({
  finding,
}: {
  finding: AdminTransactionMonitoringFindingDetailFragment;
}) {
  const itemRows = (finding.items ?? []).map((item) =>
    getFindingItemRow(item.id, item.target),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Linked Entities</CardTitle>
        <CardDescription>
          Entities linked to this finding and the routes they block or
          implicate.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {itemRows.length > 0 ? (
          <DataTable
            data={itemRows}
            columns={itemColumns}
            hidePagination={true}
            pageSize={itemRows.length}
            className="overflow-hidden border"
            showCsvExport={false}
          />
        ) : (
          <Txt variant="body">No finding items recorded.</Txt>
        )}
      </CardContent>
    </Card>
  );
}

function FindingEventLogCard({
  finding,
}: {
  finding: AdminTransactionMonitoringFindingDetailFragment;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Log</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {finding.events ? (
          <TimeLog entries={finding.events.map(entryFromEventFragment)} />
        ) : (
          <Txt variant="body">No events recorded.</Txt>
        )}
      </CardContent>
    </Card>
  );
}

function ClearFindingAction({
  finding,
}: {
  finding: AdminTransactionMonitoringFindingDetailFragment;
}) {
  const client = useApolloClient();
  const [open, setOpen] = useState(false);
  const [clearedReason, setClearedReason] = useState("");
  const [clearFinding, { loading }] = useMutation(
    graphql(`
      mutation AdminClearTransactionMonitoringFinding($input: ClearTransactionMonitoringFindingInput!) {
        clearTransactionMonitoringFinding(input: $input) {
          transactionMonitoringFinding {
            ...AdminTransactionMonitoringFindingDetail
          }
        }
      }
    `),
  );
  const isActionAvailable = finding.status !== "CLEARED";

  const submitClear = async () => {
    const normalizedReason = clearedReason.trim();

    if (normalizedReason.length < 1) {
      return;
    }

    const defaultErrorMessage =
      "There was an error clearing the finding. Please try again later.";

    await clearFinding({
      variables: {
        input: {
          id: finding.id,
          clearedReason: normalizedReason,
        },
      },
      onCompleted: (data) => {
        if (
          !data.clearTransactionMonitoringFinding?.transactionMonitoringFinding
        ) {
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to clear finding",
            description: defaultErrorMessage,
          });
          return;
        }
        client.cache.evict({ fieldName: "transactionMonitoringFindings" });
        client.cache.evict({ fieldName: "transactionQueue" });
        for (const item of finding.items ?? []) {
          if (item.target && "id" in item.target) {
            client.cache.evict({
              id: `${item.target.__typename}:${item.target.id}`,
            });
          }
        }
        toast({
          kind: "success",
          autoHide: true,
          title: "Finding cleared",
          description: `Finding #${finding.shortRef} has been cleared.`,
        });
        setOpen(false);
        setClearedReason("");
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to clear finding",
          description: error.graphQLErrors[0]?.message ?? defaultErrorMessage,
        });
      },
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setClearedReason("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={!isActionAvailable}>
          Clear
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Clear finding</DialogTitle>
          <DialogDescription>
            Add the reason this finding can be cleared. This will be recorded on
            the finding.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <label className="font-medium text-sm" htmlFor="clearedReason">
            Clear reason
          </label>
          <Textarea
            id="clearedReason"
            value={clearedReason}
            onChange={(event) => setClearedReason(event.target.value)}
            placeholder="Describe why this finding can be cleared."
            rows={5}
          />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleOpenChange(false)}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => void submitClear()}
            disabled={clearedReason.trim().length < 1}
            loading={loading}
          >
            Clear finding
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EscalateFindingAction({
  finding,
}: {
  finding: AdminTransactionMonitoringFindingDetailFragment;
}) {
  const client = useApolloClient();
  const [open, setOpen] = useState(false);
  const [escalateFinding, { loading }] = useMutation(
    graphql(`
      mutation AdminEscalateTransactionMonitoringFinding($input: EscalateTransactionMonitoringFindingInput!) {
        escalateTransactionMonitoringFinding(input: $input) {
          transactionMonitoringFinding {
            ...AdminTransactionMonitoringFindingDetail
          }
        }
      }
    `),
  );
  const isActionAvailable = finding.status === "OPEN";

  const submitEscalation = async () => {
    const defaultErrorMessage =
      "There was an error escalating the finding. Please try again later.";

    await escalateFinding({
      variables: {
        input: {
          id: finding.id,
        },
      },
      onCompleted: (data) => {
        if (
          !data.escalateTransactionMonitoringFinding
            ?.transactionMonitoringFinding
        ) {
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to escalate finding",
            description: defaultErrorMessage,
          });
          return;
        }
        client.cache.evict({ fieldName: "transactionMonitoringFindings" });
        toast({
          kind: "success",
          autoHide: true,
          title: "Finding escalated",
          description: `Finding #${finding.shortRef} has been escalated.`,
        });
        setOpen(false);
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to escalate finding",
          description: error.graphQLErrors[0]?.message ?? defaultErrorMessage,
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={!isActionAvailable}>
          Escalate
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Escalate finding</DialogTitle>
          <DialogDescription>
            Confirm that this finding should move into the escalated state.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm">
          This keeps the finding open for investigation and records the status
          transition in the activity log.
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => void submitEscalation()}
            loading={loading}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
